import { Box, Button, Container, Paper, Typography } from "@mui/material";
import { DataTable } from "modules/common";
import { useState } from "react";
import EmployeeDialog from "./components/EmployeeDialog";
import { useCreateEmployeeMutation, useDeleteEmployeeMutation, useGetEmployeesQuery, useUpdateEmployeeMutation } from "api/adminSlice";
import { useTranslation } from "react-i18next";
import { cloneDeep, set } from "lodash";
import { Add } from "@mui/icons-material";
import moment from "moment";

const $COLUMN_WIDTH = 200
export default function AdminEmployees() {
    const { t } = useTranslation()

    const [page, setPage] = useState(1)

    //=================Employee API hooks==================
    const {
        data: employees, isLoading: employeesLoading, isFetching: employeesFetching, refetch: refetchEmployees
    } = useGetEmployeesQuery({ page: 1, limit: 100 })

    const [createEmployee,
        {
            isLoading: createEmployeeLoading,
            isSuccess: createEmployeeIsSuccess,
            error: createEmployeeError,
            reset: createReset
        }
    ] = useCreateEmployeeMutation()

    const [updateEmployee,
        {
            isLoading: updateEmployeeLoading,
            isSuccess: updateEmployeeIsSuccess,
            error: updateEmployeeError,
            reset: updateReset
        }
    ] = useUpdateEmployeeMutation()

    const [deleteEmployee,
        {
            isLoading: deleteEmployeeLoading,
            isSuccess: deleteEmployeeIsSuccess,
            error: deleteEmployeeError,
            reset: deleteReset
        }
    ] = useDeleteEmployeeMutation()

    //==========Get and navigate through service points==========
    const pageChangeHandler = ({ page }) => {
        if (!employeesFetching || !employeesLoading) {
            setPage(page)
        }
    }

    //===============Handle service point values change==============
    const [selectedEmployee, setSelectedEmployee] = useState({ name: "", phone: "", password: "" })

    const resetSelectedEmployee = () => {
        setSelectedEmployee({ name: "", phone: "", password: "" })
    }

    const handleChange = ({ target: { value, name } }) => {

        setSelectedEmployee((prevState) => {
            let payload = cloneDeep(prevState)
            payload = set(payload, name, value)
            return payload
        })
    }

    //==============Employee dialog handlers==============
    const [isEditing, setIsEditing] = useState(false)

    const [openEmployeeDialog, setOpenEmployeeDialog] = useState(false)

    const resetAPIHooks = () => {
        createReset()
        updateReset()
        deleteReset()
    }

    const openDialogForCreateEmployee = () => {
        resetAPIHooks()
        setIsEditing(false)
        setOpenEmployeeDialog(true)
    }

    const openDialogForUpdateEmployee = (data) => {
        resetAPIHooks()
        data = cloneDeep(data)
        setSelectedEmployee(data)
        setIsEditing(true)
        setOpenEmployeeDialog(true)
    }

    const closeDialogHandler = () => {
        setOpenEmployeeDialog(false)
        if (isEditing) {
            resetSelectedEmployee()
        }
        setIsEditing(false)
    }


    //================Employee CRUD handlers===============
    const createEmployeeHandler = async (payload) => {
        let response = await createEmployee(payload).unwrap()
        checkEmployeeActionSuccess(response)
    }
    const updateEmployeeHandler = async (payload) => {
        let response = await updateEmployee(payload).unwrap()
        checkEmployeeActionSuccess(response)
    }
    const deleteEmployeeHandler = async (id) => {
        let response = await deleteEmployee(id).unwrap()
        checkEmployeeActionSuccess(response)
    }
    const checkEmployeeActionSuccess = (response) => {
        if (response?.success) {
            refetchEmployees()
            setTimeout(() => {
                setOpenEmployeeDialog(false)
                resetSelectedEmployee()
            }, 2000);
        }
    }

    const columns = [
        { field: "name", headerName: t("Full Name"), width: $COLUMN_WIDTH * 1.5, },
        { field: "phone", headerName: t("Phone"), renderCell: ({ value }) => (<bdi>{value}</bdi>), width: $COLUMN_WIDTH * 1.5 },
        { field: "createdAt", headerName: t("Created At"), valueGetter: ({ value }) => moment(value).format("DD/MM/YY - h:mm A"), width: $COLUMN_WIDTH * 1.5 },
    ];

    return (
        <Container>
            <Box display="flex" justifyContent="space-between" alignItems="center" my={25}>
                <Typography variant='h6'>{t("Employees")}</Typography>
                <Button
                    startIcon={<Add />}
                    sx={{ textTransform: "none" }}
                    variant="contained"
                    onClick={openDialogForCreateEmployee}
                >
                    {t("Add")}
                </Button>
            </Box>
            <Paper sx={{ height: "80vh" }}>
                <DataTable
                    onPaginationModelChange={pageChangeHandler}
                    paginationModel={{ page: page - 1, pageSize: 100 }}
                    rows={employees?.docs ?? []}
                    columns={columns}
                    rowCount={employees?.totalDocs ?? 0}
                    loading={employeesLoading || employeesFetching}
                    onRowClick={({ row }) => openDialogForUpdateEmployee(row)}
                />
            </Paper>
            <EmployeeDialog
                openEmployeeDialog={openEmployeeDialog}
                loading={createEmployeeLoading || updateEmployeeLoading || deleteEmployeeLoading}
                isEditing={isEditing}
                employee={selectedEmployee}
                handleChange={handleChange}
                onCreateEmployee={createEmployeeHandler}
                onUpdateEmployee={updateEmployeeHandler}
                onDeleteEmployee={deleteEmployeeHandler}
                onCloseDialog={closeDialogHandler}
                error={createEmployeeError || updateEmployeeError || deleteEmployeeError}
                isSuccess={createEmployeeIsSuccess || updateEmployeeIsSuccess || deleteEmployeeIsSuccess}
            />
        </Container>
    )
}
